import { getFirestore, collection, getDocs, Firestore } from 'firebase/firestore';
import type { IDataService } from '@breath-lab/services';
import type {
  Activity,
  Achievement,
  TrainingBenefit,
  Breathwork,
  Feeling,
  Journey,
  PublicBreathwork,
  PublicJourney,
} from '@breath-lab/types';
import { firebaseApp } from '../firebase/firebaseConfig';
import { FIRESTORE_COLLECTIONS } from '../firebase/firestoreCollections';
import {
  activityFromFirestore,
  achievementFromFirestore,
  benefitFromFirestore,
  breathworkFromFirestore,
  feelingFromFirestore,
  publicBreathworkFromFirestore,
  publicJourneyFromFirestore,
  journeyFromFirestore,
} from '../firebase/firestoreParsers';

export class WebDataService implements IDataService {
  private db: Firestore;

  constructor() {
    this.db = getFirestore(firebaseApp);
  }

  async fetchBreathworks(): Promise<Breathwork[]> {
    const snap = await getDocs(collection(this.db, FIRESTORE_COLLECTIONS.breathworks));
    return snap.docs
      .map((doc) => breathworkFromFirestore({ id: doc.id, ...doc.data() }))
      .filter((b) => b.isEnabled);
  }

  async fetchActivities(): Promise<Activity[]> {
    const snap = await getDocs(collection(this.db, FIRESTORE_COLLECTIONS.activities));
    return snap.docs.map((doc) => activityFromFirestore({ id: doc.id, ...doc.data() }));
  }

  async fetchBenefits(): Promise<TrainingBenefit[]> {
    const snap = await getDocs(collection(this.db, FIRESTORE_COLLECTIONS.benefits));
    return snap.docs.map((doc) => benefitFromFirestore({ id: doc.id, ...doc.data() }));
  }

  async fetchBreathworksPublic(): Promise<Breathwork[]> {
    const snap = await getDocs(collection(this.db, FIRESTORE_COLLECTIONS.publicBreathworks));
    return snap.docs
      .map((doc) => breathworkFromFirestore({ id: doc.id, ...doc.data() }))
      .filter((b) => b.isEnabled);
  }

  async fetchPublicBreathworks(): Promise<PublicBreathwork[]> {
    const snap = await getDocs(collection(this.db, FIRESTORE_COLLECTIONS.publicBreathworks));
    return snap.docs
      .filter((doc) => doc.data()['name'])
      .map((doc) => publicBreathworkFromFirestore({ id: doc.id, ...doc.data() }));
  }

  async fetchFeelings(): Promise<Feeling[]> {
    const snap = await getDocs(collection(this.db, FIRESTORE_COLLECTIONS.feelings));
    return snap.docs.map((doc) => feelingFromFirestore({ id: doc.id, ...doc.data() }));
  }

  async fetchAchievements(): Promise<Achievement[]> {
    const snap = await getDocs(collection(this.db, FIRESTORE_COLLECTIONS.achievements));
    return snap.docs
      .map((doc) => achievementFromFirestore({ id: doc.id, ...doc.data() }))
      .filter((a) => a.isEnabled);
  }

  async fetchJourneysPublic(): Promise<Journey[]> {
    const snap = await getDocs(collection(this.db, FIRESTORE_COLLECTIONS.publicJourneys));
    return snap.docs
      .map((doc) => journeyFromFirestore({ id: doc.id, ...doc.data() }))
      .filter((j) => j.isEnabled);
  }

  async fetchPublicJourneys(): Promise<PublicJourney[]> {
    const snap = await getDocs(collection(this.db, FIRESTORE_COLLECTIONS.publicJourneys));
    return snap.docs
      .map((doc) => publicJourneyFromFirestore({ id: doc.id, ...doc.data() }))
      .sort((a, b) => a.position - b.position);
  }

  async fetchJourneyTemplates(): Promise<Journey[]> {
    const snap = await getDocs(collection(this.db, FIRESTORE_COLLECTIONS.journeys));
    return snap.docs
      .map((doc) => journeyFromFirestore({ id: doc.id, ...doc.data() }))
      .filter((j) => j.isEnabled);
  }
}
